import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { avatarStyle, profileInitial } from "../shared/colors.js";
import { Profile } from "../shared/type.app.js";
import { hintLength, mathPairsForLevel, questionFromPair } from "../shared/math.js";
import { SPELLING_CURRICULUM } from "../shared/spelling-curriculum.js";
import {
  formatTeacherDate,
  recentLabel,
  subjectSummary,
  TeacherMeter,
  TeacherRecent,
  teacherSnapshot,
} from "../shared/teacher.js";
import { StoreController } from "./controller.store.js";
import { WarningEvent } from "./event.warning.js";
import { backIcon } from "./icons.js";
import { navigate, profileIdFromPath, profileSubjectFromPath } from "./nav.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { dispatch } from "./util.events.js";
import "./component.instructor-gate.js";
import "./component.level-badge.js";

@customElement("reading-bee-edit-profile")
export class ReadingBeeEditProfile extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        min-height: 100%;
      }

      .page {
        min-height: 100dvh;
        min-width: 0;
        max-width: 100%;
        overflow-x: clip;
        display: grid;
        grid-template-rows: auto 1fr;
      }

      header {
        position: sticky;
        top: 0;
        z-index: 2;
        display: grid;
        grid-template-columns: 44px 1fr 44px;
        align-items: center;
        padding: calc(0.7rem + var(--inset-top)) 1rem 0.8rem;
        background: linear-gradient(to bottom, #0c0b09 70%, rgba(12, 11, 9, 0.86));
        border-bottom: 1px solid var(--color-panel-border);
      }

      header h1 {
        margin: 0;
        font-size: 1.2rem;
        text-align: center;
      }

      .back {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--color-primary-text);
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .body {
        width: min(720px, 100%);
        min-width: 0;
        margin: 0 auto;
        padding: 1.1rem 1.2rem calc(6.5rem + env(safe-area-inset-bottom));
        display: grid;
        gap: 0.9rem;
        align-content: start;
      }

      .hero {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 0.9rem;
        align-items: center;
        padding: 1rem 1.05rem;
        border-radius: 22px;
        background: radial-gradient(420px 160px at 0% 0%, rgba(232, 184, 74, 0.16), transparent 62%), #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .ring {
        width: 84px;
        height: 84px;
        border-radius: 50%;
        padding: 4px;
        background: conic-gradient(var(--color-1) calc(var(--progress) * 1%), #2a251e 0);
        display: grid;
        place-items: center;
        flex: 0 0 auto;
      }

      .avatar {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        font-weight: 800;
        font-size: 1.8rem;
        line-height: 1;
        box-shadow: inset 0 0 0 3px #1a1713;
      }

      .who {
        min-width: 0;
      }

      .who h2 {
        margin: 0 0 0.15rem;
        font-size: 1.45rem;
      }

      .who p {
        margin: 0;
        font-size: 0.88rem;
      }

      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.35rem;
        margin-top: 0.5rem;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        padding: 0.18rem 0.55rem;
        border-radius: 999px;
        background: rgba(232, 184, 74, 0.12);
        border: 1px solid rgba(232, 184, 74, 0.32);
        color: var(--color-1);
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .chip.mix {
        color: var(--color-practice);
        background: rgba(90, 166, 232, 0.12);
        border-color: rgba(90, 166, 232, 0.35);
      }

      .chip.boost {
        color: var(--color-warning);
        background: rgba(240, 195, 106, 0.12);
        border-color: rgba(240, 195, 106, 0.35);
      }

      .progress {
        padding: 1.1rem 1.15rem 1.2rem;
        border-radius: 22px;
        background: radial-gradient(480px 180px at 100% 0%, rgba(232, 184, 74, 0.14), transparent 58%), #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .kicker {
        margin: 0 0 0.35rem;
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--color-1);
      }

      .progress-top {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 0.35rem;
      }

      .figure {
        font-size: 2.6rem;
        font-weight: 800;
        letter-spacing: -0.05em;
        line-height: 0.95;
        color: var(--color-1);
      }

      .levels {
        display: grid;
        justify-items: end;
        gap: 0.1rem;
        font-size: 0.78rem;
        font-weight: 700;
        color: var(--color-primary-text-muted);
        padding-bottom: 0.2rem;
      }

      .levels b {
        color: var(--color-primary-text);
        font-size: 0.92rem;
      }

      .progress h3 {
        margin: 0 0 0.35rem;
        font-size: 1.28rem;
        letter-spacing: -0.03em;
      }

      .progress .lede {
        margin: 0 0 1rem;
        font-size: 0.92rem;
      }

      .meter {
        display: grid;
        gap: 0.35rem;
        margin-bottom: 0.85rem;
      }

      .meter:last-of-type {
        margin-bottom: 0.55rem;
      }

      .meter-top {
        display: flex;
        justify-content: space-between;
        gap: 0.6rem;
        font-size: 0.8rem;
        font-weight: 700;
      }

      .meter-top span {
        color: var(--color-primary-text-muted);
        font-weight: 600;
      }

      .track {
        height: 10px;
        border-radius: 999px;
        background: #2a251e;
        overflow: hidden;
      }

      .track > span {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, var(--color-1), #f3d27a);
      }

      .note {
        margin: 0.15rem 0 0;
        font-size: 0.82rem;
        line-height: 1.4;
      }

      .now {
        padding: 0.95rem 1.05rem 1.05rem;
        border-radius: 20px;
        background: #120f0c;
        border: 1px solid var(--color-panel-border);
      }

      .now .prompt {
        margin: 0.15rem 0 0;
        font-family: var(--font-reading);
        font-size: 1.35rem;
        font-weight: 500;
        color: var(--color-primary-text);
        line-height: 1.35;
      }

      .recent {
        display: grid;
        gap: 0.45rem;
      }

      .pills {
        display: grid;
        gap: 0.45rem;
      }

      .pill {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 0.65rem;
        padding: 0.55rem 0.75rem;
        border-radius: 14px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
        min-width: 0;
      }

      .mark {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--color-primary-text-muted);
        box-shadow: 0 0 0 3px rgba(138, 129, 117, 0.16);
      }

      .pill.right .mark {
        background: var(--color-success);
        box-shadow: 0 0 0 3px rgba(125, 206, 130, 0.18);
      }

      .pill.wrong .mark {
        background: var(--color-practice);
        box-shadow: 0 0 0 3px rgba(90, 166, 232, 0.18);
      }

      .pill.wayTooEasy .mark {
        background: var(--color-1);
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.18);
      }

      .pill .txt {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: var(--font-reading);
        font-weight: 500;
      }

      .pill .kind {
        color: var(--color-primary-text-muted);
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .stats {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.55rem;
      }

      @media (min-width: 640px) {
        .stats {
          grid-template-columns: repeat(6, minmax(0, 1fr));
        }
      }

      .stat {
        display: grid;
        justify-items: center;
        text-align: center;
        gap: 0.12rem;
        padding: 0.75rem 0.35rem;
        border-radius: 16px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .stat b {
        font-size: 1.15rem;
        color: var(--color-primary-text);
      }

      .stat span {
        color: var(--color-primary-text-muted);
        font-size: 0.7rem;
        font-weight: 700;
      }

      .split {
        display: grid;
        gap: 0.9rem;
      }

      @media (min-width: 640px) {
        .split {
          grid-template-columns: 1fr 1fr;
        }
      }

      .panel {
        padding: 1rem 1.05rem;
        border-radius: 20px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .panel h3 {
        margin: 0 0 0.75rem;
        font-size: 0.95rem;
      }

      .activity-grid {
        display: grid;
        gap: 0 1.2rem;
      }

      @media (min-width: 640px) {
        .activity-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      .row {
        display: flex;
        justify-content: space-between;
        gap: 0.8rem;
        padding: 0.42rem 0;
        border-bottom: 1px solid rgba(244, 234, 213, 0.06);
        font-size: 0.9rem;
      }

      .row:last-child {
        border-bottom: 0;
        padding-bottom: 0;
      }

      .row span {
        color: var(--color-primary-text-muted);
      }

      .row b {
        font-weight: 800;
        text-align: right;
      }

      .admin {
        display: grid;
        gap: 0.55rem;
      }

      .admin input {
        font-size: 1.05rem;
        padding-right: 2.9rem;
      }

      .name-field {
        position: relative;
      }

      .name-check {
        position: absolute;
        right: 0.85rem;
        top: 50%;
        width: 22px;
        height: 22px;
        display: grid;
        place-items: center;
        color: var(--color-success);
        pointer-events: none;
        transform: translateY(-50%);
      }

      .name-check svg {
        width: 22px;
        height: 22px;
        overflow: visible;
      }

      .name-check path {
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
      }

      .name-check.show .arm-down {
        animation: draw-check 200ms cubic-bezier(0.2, 0, 0, 1) forwards;
      }

      .name-check.show .arm-up {
        animation: draw-check 320ms cubic-bezier(0.2, 0.7, 0.2, 1) 160ms forwards;
      }

      @keyframes draw-check {
        from {
          stroke-dashoffset: 1;
        }
        to {
          stroke-dashoffset: 0;
        }
      }

      .start-btn {
        width: 100%;
      }

      .subject-sections {
        display: grid;
        gap: 0.8rem;
      }

      .subject-section {
        display: grid;
        gap: 0.7rem;
      }

      .subject-section-top {
        display: flex;
        align-items: start;
        justify-content: space-between;
        gap: 1rem;
      }

      .subject-section h3,
      .subject-section p {
        margin: 0;
      }

      .subject-section > p {
        color: var(--color-primary-text-muted);
        font-size: 0.9rem;
      }

      .subject-progress {
        display: grid;
        gap: 0.55rem;
        margin-top: 0.9rem;
        padding-top: 0.85rem;
        border-top: 1px solid rgba(244, 234, 213, 0.08);
      }

      .subject-progress-heading {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.75rem;
      }

      .subject-progress-heading strong {
        font-size: 0.86rem;
      }

      .subject-progress-heading span {
        color: var(--color-primary-text-muted);
        font-size: 0.78rem;
      }

      .subject-progress .meter {
        margin: 0;
      }

      .subject-progress-note {
        margin: 0;
        color: var(--color-primary-text-muted);
        font-size: 0.78rem;
        line-height: 1.35;
      }

      @media (prefers-reduced-motion: reduce) {
        .name-check.show path {
          animation: none;
          stroke-dashoffset: 0;
        }
      }
    `,
  ];

  @state() private profileId = "";
  @state() private name = "";
  @state() private nameSaved = false;
  private nameSaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.profileId = profileIdFromPath() ?? "";
    const profile = appStore.state.profiles.find((item) => item.id === this.profileId);
    this.name = profile?.name ?? "";
    if (!profile) {
      navigate("settings");
    }
  }

  override render(): TemplateResult {
    const profile = appStore.state.profiles.find((item) => item.id === this.profileId);
    if (!profile) {
      return html`
        <div class="page"><p>No profile found.</p></div>
      `;
    }
    const subject = profileSubjectFromPath();
    if (subject) return this.renderSubjectProgress(profile, subject);
    const snap = teacherSnapshot(profile, appStore.state.currentProfileId);
    return html`
      <div class="page">
        <header>
          <button class="back" aria-label="Back" @click=${this.close}>${backIcon}</button>
          <h1>Teacher view</h1>
        </header>
        <div class="body">
          <reading-bee-instructor-gate>
            <section class="hero">
              <div class="ring" style="--progress:${snap.progressPercent}">
                <div class="avatar" style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                  ${profileInitial(profile.name)}
                </div>
              </div>
              <div class="who">
                <h2>${profile.name}</h2>
                <p>${snap.bandLabel}</p>
                ${
                  snap.tags.length
                    ? html`
                        <div class="chips">${snap.tags.map((tag) => this.chip(tag))}</div>
                      `
                    : ""
                }
              </div>
              <reading-bee-level-badge .level=${profile.level}></reading-bee-level-badge>
            </section>

            <section class="subject-sections" aria-label="Subject progress">
              ${this.subjectSection(profile, "reading")} ${this.subjectSection(profile, "math")}
              ${this.subjectSection(profile, "spelling")}
            </section>

            <section class="panel">
              <h3>Rewards & activity</h3>
              <div class="activity-grid">
                <div class="row">
                  <span>Coins on hand</span>
                  <b>${snap.coins}</b>
                </div>
                <div class="row">
                  <span>Coins earned</span>
                  <b>${snap.coinsEarned}</b>
                </div>
                <div class="row">
                  <span>Peak in bank</span>
                  <b>${snap.peakCoins}</b>
                </div>
                <div class="row">
                  <span>Coins spent</span>
                  <b>${snap.spent}</b>
                </div>
                <div class="row">
                  <span>Shop items</span>
                  <b>${snap.items}</b>
                </div>
                <div class="row">
                  <span>Achievements</span>
                  <b>${snap.achievements} / ${snap.achievementTotal}</b>
                </div>
                <div class="row">
                  <span>Today</span>
                  <b>${snap.readsToday} read${snap.readsToday === 1 ? "" : "s"}</b>
                </div>
                <div class="row">
                  <span>This week</span>
                  <b>${snap.readsWeek}</b>
                </div>
                <div class="row">
                  <span>Last active</span>
                  <b>${snap.lastActiveAt ? formatTeacherDate(snap.lastActiveAt) : "Not yet"}</b>
                </div>
                <div class="row">
                  <span>Added</span>
                  <b>${formatTeacherDate(snap.createdAt)}</b>
                </div>
              </div>
            </section>

            <section class="panel admin">
              <h3>Teacher tools</h3>
              <div class="name-field">
                <input
                  type="text"
                  maxlength="40"
                  aria-label="Student name"
                  .value=${this.name}
                  @input=${this.onName}
                  @keydown=${this.onKey}
                  @blur=${this.flushNameSave} />
                <span
                  class="name-check${this.nameSaved ? " show" : ""}"
                  role="status"
                  aria-live="polite"
                  aria-hidden=${this.nameSaved ? "false" : "true"}>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      class="arm-down"
                      d="M4 12L9 17"
                      pathLength="1"
                      stroke="currentColor"
                      stroke-width="2.8"
                      stroke-linecap="round" />
                    <path
                      class="arm-up"
                      d="M9 17L20 6"
                      pathLength="1"
                      stroke="currentColor"
                      stroke-width="2.8"
                      stroke-linecap="round" />
                  </svg>
                </span>
              </div>
            </section>
          </reading-bee-instructor-gate>
        </div>
      </div>
    `;
  }

  private chip(tag: string): TemplateResult {
    const kind = tag === "Support mix" ? "mix" : tag === "Boost" ? "boost" : "";
    return html`
      <span class="chip ${kind}">${tag}</span>
    `;
  }

  private renderSubjectProgress(profile: Profile, subject: "reading" | "math" | "spelling"): TemplateResult {
    const progress = profile.subjectProgress?.[subject];
    const title =
      subject === "reading" ? "Reading progress" : subject === "math" ? "Math progress" : "Spelling progress";
    const snap = teacherSnapshot(profile, appStore.state.currentProfileId);
    if (!progress) {
      return this.progressPage(
        profile,
        title,
        html`
          <section class="panel"><p>No progress yet.</p></section>
        `,
      );
    }
    if (subject === "reading") {
      return this.progressPage(
        profile,
        title,
        html`
          <section class="progress">
            <p class="kicker">Next level</p>
            <div class="progress-top">
              <div class="figure">${snap.figure}</div>
              <div class="levels">
                <span>Badge ${snap.level}</span>
                <b>Level ${snap.nextLevel}</b>
              </div>
            </div>
            <h3>${snap.headline}</h3>
            <p class="lede">${snap.detail}</p>
            ${snap.meters.map((meter) => this.meter(meter))}
            <p class="note">${snap.note}</p>
          </section>
          ${
            snap.currentText
              ? html`
                  <section class="now">
                    <p class="kicker">Now reading</p>
                    <p class="prompt">${snap.currentText}</p>
                  </section>
                `
              : ""
          }
          ${
            snap.recent.length
              ? html`
                  <section class="panel recent">
                    <h3>Recent answers</h3>
                    <div class="pills">${snap.recent.map((item) => this.recent(item))}</div>
                  </section>
                `
              : ""
          }
          <section class="stats">
            ${this.stat(snap.stats.read, "Texts")}${this.stat(snap.stats.right, "Right")}${this.stat(snap.stats.wrong, "Wrong")}${this.stat(snap.stats.skip, "Skipped")}${this.stat(snap.stats.wayTooEasy, "Easy")}${this.stat(snap.stats.accuracy, "Accuracy")}
          </section>
          <section class="panel">
            <h3>Learning</h3>
            <div class="row">
              <span>Badge level</span>
              <b>${snap.level}</b>
            </div>
            <div class="row">
              <span>Working level</span>
              <b>${snap.workingLevel}</b>
            </div>
            <div class="row">
              <span>Best streak</span>
              <b>${snap.maxCorrectStreak}</b>
            </div>
            <div class="row">
              <span>Mastered texts</span>
              <b>${snap.mastered}</b>
            </div>
            <div class="row">
              <span>Support mix</span>
              <b>${snap.reviewMode ? `On · ${snap.wrongStreak} miss${snap.wrongStreak === 1 ? "" : "es"}` : "Off"}</b>
            </div>
            <div class="row">
              <span>Boost</span>
              <b>${snap.boostActive ? `On · level ${snap.workingLevel}` : "Off"}</b>
            </div>
          </section>
        `,
      );
    }
    if (subject === "math") {
      const right = progress.events.filter((event) => event.result === "right" || event.result === "wayTooEasy").length;
      const wrong = progress.events.filter((event) => event.result === "wrong").length;
      return this.progressPage(
        profile,
        title,
        html`
          <section class="panel">
            <p class="kicker">Current level</p>
            <div class="figure">${progress.level}</div>
            <div class="stats">
              ${this.stat(progress.events.length, "Answers")}${this.stat(right, "Right")}${this.stat(wrong, "Wrong")}
            </div>
          </section>
        `,
      );
    }
    const curriculum = SPELLING_CURRICULUM[progress.level - 1] ?? SPELLING_CURRICULUM[0];
    const mastered = curriculum.words.filter((word) => (progress.itemCorrect[word] ?? 0) >= 2).length;
    const correctsRemaining = curriculum.words.reduce(
      (total, word) => total + Math.max(0, 2 - (progress.itemCorrect[word] ?? 0)),
      0,
    );
    const recentSpelling = progress.events.slice(-12).reverse();
    return this.progressPage(
      profile,
      title,
      html`
        <section class="progress">
          <p class="kicker">Level ${curriculum.level} · ${curriculum.typicalGrade}</p>
          <h3>${curriculum.title}</h3>
          <p class="lede">${curriculum.pattern}</p>
          ${this.meter({ label: "Words mastered", value: mastered, max: curriculum.words.length })}
          <div class="row">
            <span>Correct answers remaining</span>
            <b>${correctsRemaining}</b>
          </div>
          <div class="row">
            <span>Correct streak</span>
            <b>${progress.correctStreak} / ${curriculum.words.length}</b>
          </div>
        </section>
        <section class="panel">
          <h3>Current words</h3>
          <div class="pills">
            ${curriculum.words.map(
              (word) => html`
                <div class="pill">
                  <span class="mark"></span>
                  <span class="txt">${word}</span>
                  <span class="kind">${progress.itemCorrect[word] ?? 0} / 2</span>
                </div>
              `,
            )}
          </div>
        </section>
        ${
          recentSpelling.length
            ? html`
                <section class="panel recent">
                  <h3>Recent spelling answers</h3>
                  <div class="pills">
                    ${recentSpelling.map(
                      (event) => html`
                        <div class="pill ${event.result}">
                          <span class="mark"></span>
                          <span class="txt">${event.itemId ?? "Older answer"}</span>
                          <span class="kind">${recentLabel(event.result)}</span>
                        </div>
                      `,
                    )}
                  </div>
                </section>
              `
            : ""
        }
      `,
    );
  }

  private progressPage(profile: Profile, title: string, content: TemplateResult): TemplateResult {
    return html`
      <div class="page">
        <header>
          <button class="back" aria-label="Back" @click=${this.close}>${backIcon}</button>
          <h1>${title}</h1>
        </header>
        <div class="body">
          <reading-bee-instructor-gate>
            <section class="hero">
              <div class="avatar" style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                ${profileInitial(profile.name)}
              </div>
              <div class="who">
                <h2>${profile.name}</h2>
                <p>${title}</p>
              </div>
            </section>
            ${content}
          </reading-bee-instructor-gate>
        </div>
      </div>
    `;
  }

  private subjectSection(profile: Profile, subject: "reading" | "math" | "spelling"): TemplateResult {
    const summary = subjectSummary(profile, subject);
    const studentName = this.name.trim() || profile.name;
    const action =
      subject === "reading"
        ? `Start reading with ${studentName}`
        : subject === "math"
          ? `Start doing math with ${studentName}`
          : `Start spelling with ${studentName}`;
    return html`
      <section class="panel subject-section">
        <div class="subject-section-top">
          <div>
            <p class="kicker">${summary.label}</p>
            <h3>${summary.participated ? `Level ${summary.level}` : `Not participating in ${summary.label}`}</h3>
          </div>
          ${
            summary.participated
              ? html`
                  <strong>${summary.answers} answer${summary.answers === 1 ? "" : "s"}</strong>
                `
              : ""
          }
        </div>
        <p>
          ${
            summary.participated
              ? `${summary.right} right, ${summary.wrong} wrong, ${summary.skipped} skipped${
                  summary.easy > 0 ? `, ${summary.easy} easy` : ""
                }.`
              : `${studentName} has not participated in ${summary.label} yet.`
          }
        </p>
        ${this.subjectProgressSummary(profile, subject)}
        <div class="button-bar subject-actions">
          <button class="ghost-btn" @click=${() => this.viewProgress(subject)}>View progress</button>
          <button class="ghost-btn" @click=${() => this.startSubject(subject)}>${action}</button>
        </div>
      </section>
    `;
  }

  private viewProgress = (subject: "reading" | "math" | "spelling"): void => {
    navigate("profile-progress", this.profileId, { subject });
  };

  private subjectProgressSummary(profile: Profile, subject: "reading" | "math" | "spelling"): TemplateResult {
    if (!profile.subjectProgress?.[subject]) return html``;
    if (subject === "reading") {
      const snap = teacherSnapshot(profile, appStore.state.currentProfileId);
      return html`
        <div class="subject-progress">
          <div class="subject-progress-heading">
            <strong>${snap.atMaxLevel ? "Reading path complete" : `Next: level ${snap.nextLevel}`}</strong>
            <span>${snap.atMaxLevel ? "100%" : `${snap.progressPercent}%`}</span>
          </div>
          ${snap.atMaxLevel ? "" : this.meter({ label: snap.headline, value: snap.progressPercent, max: 100 })}
          <p class="subject-progress-note">${snap.atMaxLevel ? snap.detail : snap.detail}</p>
        </div>
      `;
    }

    const progress = profile.subjectProgress[subject];
    if (subject === "math") {
      const level = progress.level;
      const atMaxLevel = level >= 100;
      const currentPairs = mathPairsForLevel(level);
      const currentIds = new Set(currentPairs.map((pair) => questionFromPair(level, pair).id));
      const seenIds = Object.keys(progress.mathSeen).filter((id) => currentIds.has(id));
      const generatedAddition = level >= 12 && level <= 25;
      const seen = generatedAddition
        ? Object.keys(progress.mathSeen).filter((id) => id.startsWith(`${level}-addition-`)).length
        : seenIds.filter((id) => {
            const question = currentPairs.map((pair) => questionFromPair(level, pair)).find((item) => item.id === id);
            return (
              question?.operation !== "multiplication" || (progress.mathHintHidden[id] ?? 0) >= hintLength(question)
            );
          }).length;
      const needed = generatedAddition ? 50 : currentPairs.length;
      const recent = progress.events.slice(-20);
      const recentCorrect = recent.filter((event) => event.result === "right" || event.result === "wayTooEasy").length;
      const lastFive = progress.events.slice(-5);
      const lastFiveCorrect =
        lastFive.length === 5 && lastFive.every((event) => event.result === "right" || event.result === "wayTooEasy");
      let trailingCorrect = 0;
      for (const event of [...progress.events].reverse()) {
        if (event.result !== "right" && event.result !== "wayTooEasy") break;
        trailingCorrect += 1;
      }
      const coverageComplete = seen >= needed;
      const atMaxDetail = "All 100 math levels are complete.";
      const nextDetail = coverageComplete
        ? `${Math.max(0, 20 - recent.length)} more answer${20 - recent.length === 1 ? "" : "s"} needed for the 20-answer window, ${Math.max(0, 17 - recentCorrect)} more correct in that window, and ${lastFiveCorrect ? 0 : Math.max(1, 5 - trailingCorrect)} in a row.`
        : `${needed - seen} problem${needed - seen === 1 ? "" : "s"} left in this level.`;
      return html`
        <div class="subject-progress">
          <div class="subject-progress-heading">
            <strong>${atMaxLevel ? "Math path complete" : `Next: level ${level + 1}`}</strong>
            <span>${atMaxLevel ? "100%" : `${Math.min(100, Math.round((seen / Math.max(1, needed)) * 100))}%`}</span>
          </div>
          ${atMaxLevel ? "" : this.meter({ label: "Level problems mastered", value: seen, max: needed })}
          <p class="subject-progress-note">${atMaxLevel ? atMaxDetail : nextDetail}</p>
        </div>
      `;
    }

    const curriculum = SPELLING_CURRICULUM[progress.level - 1] ?? SPELLING_CURRICULUM[0];
    const correctsRemaining = curriculum.words.reduce(
      (total, word) => total + Math.max(0, 2 - (progress.itemCorrect[word] ?? 0)),
      0,
    );
    return html`
      <div class="subject-progress">
        <div class="subject-progress-heading">
          <strong>Next: level ${Math.min(100, progress.level + 1)}</strong>
          <span>${correctsRemaining === 0 ? "Ready" : `${correctsRemaining} left`}</span>
        </div>
        ${this.meter({
          label: "Correct answers",
          value: curriculum.words.reduce((total, word) => total + Math.min(2, progress.itemCorrect[word] ?? 0), 0),
          max: curriculum.words.length * 2,
        })}
        <p class="subject-progress-note">
          ${correctsRemaining === 0 ? "The word set is complete; recent correct answers still need to hold." : `${correctsRemaining} correct answer${correctsRemaining === 1 ? "" : "s"} left for this word set.`}
        </p>
      </div>
    `;
  }

  private meter(meter: TeacherMeter): TemplateResult {
    const percent = Math.min(100, Math.round((meter.value / meter.max) * 100));
    return html`
      <div class="meter">
        <div class="meter-top">
          <strong>${meter.label}</strong>
          <span>${meter.value} / ${meter.max}</span>
        </div>
        <div class="track"><span style="width:${percent}%"></span></div>
      </div>
    `;
  }

  private recent(item: TeacherRecent): TemplateResult {
    return html`
      <div class="pill ${item.result}">
        <span class="mark"></span>
        <span class="txt">${item.text}</span>
        <span class="kind">${recentLabel(item.result)}</span>
      </div>
    `;
  }

  private stat(value: number | string, label: string): TemplateResult {
    return html`
      <div class="stat">
        <b>${value}</b>
        <span>${label}</span>
      </div>
    `;
  }

  private close = (): void => {
    this.flushNameSave();
    navigate(
      profileSubjectFromPath() ? "edit-profile" : "settings",
      profileSubjectFromPath() ? this.profileId : undefined,
    );
  };

  private onName = (event: Event): void => {
    this.name = (event.target as HTMLInputElement).value;
    this.nameSaved = false;
    this.scheduleNameSave();
  };

  private onKey = (event: KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.saveName(false);
    }
  };

  private scheduleNameSave = (): void => {
    this.clearNameSaveTimer();
    this.nameSaveTimer = setTimeout(() => {
      this.saveName(true);
    }, 1000);
  };

  private clearNameSaveTimer = (): void => {
    if (this.nameSaveTimer === null) return;
    clearTimeout(this.nameSaveTimer);
    this.nameSaveTimer = null;
  };

  private flushNameSave = (): void => {
    this.saveName(true);
  };

  private saveName = (quiet: boolean): void => {
    const pending = this.nameSaveTimer !== null;
    this.clearNameSaveTimer();
    const next = this.name.trim();
    if (!next) {
      this.nameSaved = false;
      if (!quiet) dispatch(this, WarningEvent("Enter a name"));
      return;
    }
    if (!this.profileId) return;
    const profile = appStore.state.profiles.find((item) => item.id === this.profileId);
    const changed = profile?.name !== next;
    if (changed) appStore.renameProfile(this.profileId, next);
    this.name = next;
    if (changed || pending || !quiet) this.nameSaved = true;
  };

  private startSubject = (subject: "reading" | "math" | "spelling"): void => {
    this.flushNameSave();
    if (!this.profileId) return;
    appStore.switchProfile(this.profileId);
    appStore.setSelectedSubject(subject);
    navigate("reading");
  };

  override disconnectedCallback(): void {
    this.flushNameSave();
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-edit-profile": ReadingBeeEditProfile;
  }
}
